import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  InputAdornment,
  Divider,
  Container,
  Icon,
  Alert,
  Snackbar,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import HelpIcon from "@mui/icons-material/Help";
import ShieldIcon from "@mui/icons-material/Shield";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VerifiedIcon from "@mui/icons-material/Verified";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { useSelector, useDispatch } from "react-redux";
import { GetUserInfo, UpdateUserInfo, UpdateEntityInfo } from "../../Redux/Actions/userStatusService";
import { useUser } from "../../Context/UserContext";
import { usePermissions } from "../../Context/PermissionContext";
import SecurityDetails from "../ProfileSettings/SecurityDetails";
import TeamManagement from "../ProfileSettings/TeamManagement";

function ManagerSettingsComponent() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { userRole, userInfo } = useUser();
  const { permissions, hasPermission } = usePermissions();
  const permissionsList = useSelector((state) => state.permissions || []);

  const pathname = window.location.pathname;
  const tabFromUrl = pathname.split('/')[2];
  const urlToMenuMap = {
    'account': 'Account',
    'adduser': 'Add Users',
    'privacy': 'Privacy',
    'security': 'Security',
    'appearance': 'Appearance',
    'support': 'Support'
  };
  const [activeSection, setActiveSection] = useState(urlToMenuMap[tabFromUrl] || 'Account');

  const [personalInfo, setPersonalInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    entityName: "",
    parentEmail: "",
  });

  const [userInfoData, setUserInfoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch user info on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setLoading(true);
        const response = await dispatch(GetUserInfo());
        
        if (response.payload?.statusCode === 200) {
          const userData = response.payload.data;
          setUserInfoData(userData);
          
          // Get parent info from userInfo structure
          const parentInfo = userInfo?.parents?.[0]?.parentUserRole;
          const parentEntityInfo = parentInfo?.entityInfo;
          const parentUser = parentInfo?.user;
          
          // Populate form with user data based on new API structure
          setPersonalInfo({
            firstName: userData?.userInfo?.firstName || "",
            lastName: userData?.userInfo?.lastName || "",
            email: userData?.email || "",
            entityName: parentEntityInfo?.name || userData?.entityInfo?.name || "",
            parentEmail: parentUser?.email || "",
          });
        } else {
          console.error("Failed to fetch user info:", response);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [dispatch, userInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo({
      ...personalInfo,
      [name]: value,
    });
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      if (userRole === 'SUB_MANAGER') {
        // Update user info for SUB_MANAGER
        const userInfoPayload = {
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
        };
        
        const response = await dispatch(UpdateUserInfo(userInfoPayload));
        
        if (response.payload?.statusCode === 200) {
          // Fetch updated user info after successful save
          const updatedUserResponse = await dispatch(GetUserInfo());
          if (updatedUserResponse.payload?.statusCode === 200) {
            const updatedUserData = updatedUserResponse.payload.data;
            setUserInfoData(updatedUserData);
            
            // Get parent info from userInfo structure
            const parentInfo = userInfo?.parents?.[0]?.parentUserRole;
            const parentEntityInfo = parentInfo?.entityInfo;
            const parentUser = parentInfo?.user;
            
            setPersonalInfo({
              firstName: updatedUserData?.userInfo?.firstName || "",
              lastName: updatedUserData?.userInfo?.lastName || "",
              email: updatedUserData?.email || "",
              entityName: parentEntityInfo?.name || updatedUserData?.entityInfo?.name || "",
              parentEmail: parentUser?.email || "",
            });
          }
          
          setSnackbar({
            open: true,
            message: 'Manager information updated successfully!',
            severity: 'success'
          });
        } else {
          throw new Error('Failed to update manager info');
        }
      } else if (userRole === 'MANAGER') {
        // Update entity info for MANAGER
        const entityInfoPayload = {
          name: personalInfo.entityName,
          entityType: "INVESTOR", // Default entity type
        };
        
        const response = await dispatch(UpdateEntityInfo(entityInfoPayload));
        
        if (response.payload?.statusCode === 200) {
          // Fetch updated user info after successful save
          const updatedUserResponse = await dispatch(GetUserInfo());
          if (updatedUserResponse.payload?.statusCode === 200) {
            const updatedUserData = updatedUserResponse.payload.data;
            setUserInfoData(updatedUserData);
            
            // Get parent info from userInfo structure
            const parentInfo = userInfo?.parents?.[0]?.parentUserRole;
            const parentEntityInfo = parentInfo?.entityInfo;
            const parentUser = parentInfo?.user;
            
            setPersonalInfo({
              firstName: updatedUserData?.userInfo?.firstName || "",
              lastName: updatedUserData?.userInfo?.lastName || "",
              email: updatedUserData?.email || "",
              entityName: parentEntityInfo?.name || updatedUserData?.entityInfo?.name || "",
              parentEmail: parentUser?.email || "",
            });
          }
          
          setSnackbar({
            open: true,
            message: 'Entity information updated successfully!',
            severity: 'success'
          });
        } else {
          throw new Error('Failed to update entity info');
        }
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setSnackbar({
        open: true,
        message: 'Failed to update information. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleMenuClick = (label) => {
    setActiveSection(label);
  };

  const getMenuItems = () => {
    const menuItems = [
      {
        icon: <PersonIcon />,
        label: 'Account',
        description: 'Update manager information',
        permission: 'updateEntityInfo'
      },
      {
        icon: <DarkModeIcon />,
        label: 'Appearance',
        description: 'Light or Dark mode'
      },
      {
        icon: <HelpIcon />,
        label: 'Support',
        description: 'Have questions? Reach out'
      },
    ];

    // Add "Your Team" option only if user has required permissions
    const hasTeamPermissions = hasPermission("deleteChildRelation") && 
                             hasPermission("createUser") && 
                             hasPermission("getGroupPermissions");

    
    if (hasTeamPermissions) {
      menuItems.splice(1, 0, {
        icon: <GroupAddIcon />,
        label: 'Your Team',
        description: 'Add sub-admins and managers to your team'
      });
    } 

    return menuItems;
  };

  const menuItems = getMenuItems();

  const renderContent = () => {
    switch (activeSection) {
      case "Account":
        return (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 3, 
              minHeight: '500px',
              backgroundColor: 'white',
              width: "80%",
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 600,
                fontSize: '1.25rem',
                color: '#333'
              }}
            >
              Manager Details
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: '#666',
                    mb: 1
                  }}
                >
                  Entity Name
                </Typography>
                <TextField
                  fullWidth
                  name="entityName"
                  value={personalInfo.entityName}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <BusinessIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: '#666',
                    mb: 1
                  }}
                >
                  Manager Email
                </Typography>
                <TextField
                  fullWidth
                  name="parentEmail"
                  value={personalInfo.parentEmail}
                  variant="outlined"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <EmailIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '& input': {
                        color: '#999',
                      },
                    },
                    '& .Mui-disabled': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ display: "flex", mt: 4, gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={saving || !hasPermission("updateEntityInfo")}
                sx={{
                  backgroundColor: hasPermission("updateEntityInfo") ? '#1976d2' : '#ccc',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: hasPermission("updateEntityInfo") ? '#1565c0' : '#ccc',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#666',
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save changes'}
              </Button>
              <Button
                variant="outlined"
                disabled={saving}
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  px: 3,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                  '&:disabled': {
                    borderColor: '#ccc',
                    color: '#ccc',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        );
      case "Your Team":
        return <TeamManagement />;
      case "Security":
        return <SecurityDetails />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography variant="h6" color="text.secondary">
            Loading manager settings...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" sx={{ fontWeight: 600, color: "#18275A", mt: 2 }}>
        Manager Settings
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your manager account settings and preferences.
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ width: '40%' }}>
          <Paper elevation={1} sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "#e0e0e0",
                  fontSize: "1.5rem",
                  mr: 2,
                }}
              >
                {personalInfo.firstName ? personalInfo.firstName.charAt(0) : "M"}
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 500,
                    color: "#18275A",
                  }}
                >
                  Manager
                  <VerifiedIcon
                    color="primary"
                    sx={{ ml: 0.5, width: 20, height: 20 }}
                  />
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <List component="nav" sx={{ width: '100%' }}>
              {menuItems.map((item) => (
                <ListItemButton
                  key={item.label}
                  onClick={() => handleMenuClick(item.label)}
                  selected={activeSection === item.label}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.description}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: activeSection === item.label ? 600 : 400,
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  />
                  <ChevronRightIcon
                    sx={{
                      fontSize: '1rem',
                      color: 'text.secondary',
                      opacity: activeSection === item.label ? 1 : 0.5,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Box>
        
        <Box sx={{ width: '60%' }}>
          {renderContent()}
        </Box>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ManagerSettingsComponent; 